import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

/**
 * Payment tickets on returned / disputed payments (backend:
 * admin-payment-cases). Opened from the Returned payments queue; each ticket
 * holds the evidence, the drafted email + letter, the Stripe response text
 * and a timeline.
 */

export type CaseStatus = 'open' | 'waiting_customer' | 'submitted' | 'won' | 'lost' | 'closed';

export const CASE_STATUS_LABEL: Record<CaseStatus, string> = {
  open: 'Open',
  waiting_customer: 'Waiting on customer',
  submitted: 'Submitted to Stripe',
  won: 'Won',
  lost: 'Lost',
  closed: 'Closed',
};

export interface PaymentCase {
  id: string;
  case_number: number;
  label: string;
  customer_id: string | null;
  customer_name: string | null;
  payment_return_id: string | null;
  status: CaseStatus;
  amount: number | null;
  reason_code: string | null;
  stripe_dispute_id: string | null;
  evidence_due_by: string | null;
  reply_by: string | null;
  email_to: string | null;
  email_subject: string | null;
  email_body: string | null;
  letter_title: string | null;
  letter_body: string | null;
  stripe_response: string | null;
  created_by: string | null;
  created_at: string;
}

export interface CaseDocument {
  id: string;
  kind: 'invoice' | 'signature' | 'photo' | 'upload';
  label: string;
  content_type: string;
  size_bytes: number | null;
  include: boolean;
  url: string | null;
  mailable: boolean;
}

export interface CaseEvent {
  id: string;
  action: string;
  detail: Record<string, unknown> | null;
  actor: string | null;
  created_at: string;
}

export interface CaseDetail {
  ok: true;
  case: PaymentCase;
  payment_return: Record<string, unknown> | null;
  documents: CaseDocument[];
  events: CaseEvent[];
}

export interface CaseListRow {
  id: string;
  case_number: number;
  status: CaseStatus;
  amount: number | null;
  reason_code: string | null;
  evidence_due_by: string | null;
  reply_by: string | null;
  created_at: string;
  customers: { name: string } | null;
}

export function usePaymentCases() {
  return useQuery<{ ok: true; rows: CaseListRow[] }>({
    queryKey: ['admin', 'payment-cases'],
    queryFn: () => adminFetch('admin-payment-cases'),
  });
}

export function usePaymentCase(id: string | undefined) {
  return useQuery<CaseDetail>({
    queryKey: ['admin', 'payment-case', id],
    queryFn: () => adminFetch(`admin-payment-cases?id=${encodeURIComponent(id!)}`),
    enabled: !!id,
  });
}

/** Every ticket write: POST { action, ... }, then refresh the ticket + lists. */
export function useCaseAction<T = { ok: true }>() {
  const qc = useQueryClient();
  return useMutation<T, Error, Record<string, unknown>>({
    mutationFn: (body) =>
      adminFetch<T>('admin-payment-cases', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (_d, body) => {
      if (typeof body.id === 'string') qc.invalidateQueries({ queryKey: ['admin', 'payment-case', body.id] });
      qc.invalidateQueries({ queryKey: ['admin', 'payment-cases'] });
      qc.invalidateQueries({ queryKey: ['admin', 'payment-returns'] });
    },
  });
}
